import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { TooltipDirective } from '@sb/tooltip';
import { ButtonComponent, CheckboxComponent, IconButtonComponent, IconComponent, InputModule } from '@sb/ui';
import { clsx } from 'clsx';

// We have to define some Tailwind grid-cols class explicitly because we can't use dynamic values

/* eslint-disable @typescript-eslint/naming-convention */
const gridCols = {
  1: 'grid-cols-[repeat(1,1fr),34px]',
  2: 'grid-cols-[repeat(2,1fr),34px]',
  3: 'grid-cols-[repeat(3,1fr),34px]',
  4: 'grid-cols-[repeat(4,1fr),34px]',
  5: 'grid-cols-[repeat(5,1fr),34px]',
  6: 'grid-cols-[repeat(6,1fr),34px]',
  7: 'grid-cols-[repeat(7,1fr),34px]',
  8: 'grid-cols-[repeat(8,1fr),34px]',
  9: 'grid-cols-[repeat(9,1fr),34px]',
  10: 'grid-cols-[repeat(10,1fr),34px]',
  11: 'grid-cols-[repeat(11,1fr),34px]',
  12: 'grid-cols-[repeat(12,1fr),34px]',
};

const selectableGridCols = {
  1: 'grid-cols-[32px,repeat(1,1fr),34px]',
  2: 'grid-cols-[32px,repeat(2,1fr),34px]',
  3: 'grid-cols-[32px,repeat(3,1fr),34px]',
  4: 'grid-cols-[32px,repeat(4,1fr),34px]',
  5: 'grid-cols-[32px,repeat(5,1fr),34px]',
  6: 'grid-cols-[32px,repeat(6,1fr),34px]',
  7: 'grid-cols-[32px,repeat(7,1fr),34px]',
  8: 'grid-cols-[32px,repeat(8,1fr),34px]',
  9: 'grid-cols-[32px,repeat(9,1fr),34px]',
  10: 'grid-cols-[32px,repeat(10,1fr),34px]',
  11: 'grid-cols-[32px,repeat(11,1fr),34px]',
  12: 'grid-cols-[32px,repeat(12,1fr),34px]',
};
/* eslint-enable @typescript-eslint/naming-convention */
type GridCols = keyof typeof gridCols;

type EntityFormGroup = FormGroup<{
  [key: string]: FormControl;
  removable: FormControl<boolean>;
  selected?: FormControl<boolean>;
}>;
type DynamicTableForm = FormGroup<{
  rows: FormArray<EntityFormGroup>;
  selectAll?: FormControl<boolean>;
}>;

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputModule,
    ButtonComponent,
    IconButtonComponent,
    IconComponent,
    TooltipDirective,
    CheckboxComponent,
  ],
  templateUrl: './dynamic-table.component.html',
  selector: 'dynamic-table',
})
export class DynamicTableComponent implements OnInit, OnChanges {
  @Input()
  public form: DynamicTableForm = new FormGroup({
    rows: new FormArray([]),
  });
  @Input({ required: true })
  public columns: string[];
  @Input({ required: true })
  public rowTemplate: TemplateRef<any>;
  @Input({ required: true })
  public addEntityLabel: string;
  @Input()
  public addEntityDisabledTooltipLabel: string | SafeHtml;
  @Input()
  public entityLimit = Infinity;
  @Input()
  public selectable = false;
  @Output()
  public addRow = new EventEmitter<void>();
  @Output()
  public removeRow = new EventEmitter<number>();
  @Output()
  public rowToggle = new EventEmitter<number>();
  @Output()
  public selectAllToggle = new EventEmitter<void>();

  public get rows() {
    return this.form.controls.rows;
  }

  public rowClasses: string;

  public get someSelected(): boolean {
    const selectedCount = this.selectedRowsCount;
    return selectedCount > 0 && selectedCount < this.rows.length;
  }

  public get selectedRowsCount(): number {
    return this.form.value.rows?.reduce((acc, row) => acc + (row.selected ? 1 : 0), 0) || 0;
  }

  public ngOnInit(): void {
    this.setClasses();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectable']) {
      if (this.selectable) {
        this.form.addControl('selectAll', new FormControl(this.selectedRowsCount === this.rows.length));
      } else {
        this.form.removeControl('selectAll');
      }
    }

    if (changes['columns'] || changes['selectable']) {
      this.setClasses();
    }
  }

  public toggleSelectAll() {
    this.rows.controls.forEach((row: EntityFormGroup) => {
      row.patchValue({ selected: this.form.value.selectAll });
    });
    this.selectAllToggle.emit();
  }

  private setClasses(): void {
    this.rowClasses = clsx([
      'grid',
      this.selectable ? selectableGridCols[this.columns.length as GridCols] : gridCols[this.columns.length as GridCols],
      'gap-2',
      'items-center',
    ]);
  }
}
